/**
 * DEVLOGS-TEMP — bounded FIFO that drops the OLDEST entry and counts the loss.
 *
 * Semantics are `MetricBuffer` (lib/player-shell/src/player-telemetry.ts)
 * verbatim, so a reviewer recognises it on sight: drop oldest, never newest,
 * count what was dropped.
 *
 * ONE addition, deliberate: a BYTE bound alongside the count bound. MetricBuffer
 * counts entries because a metric is `{name, ids, counters}` — bounded by shape.
 * A debug record carries a payload, and 500 records x 4 KB is 2 MB of live JS
 * objects on an Android TV box that is also holding a media cache and two audio
 * players. Count alone does not bound memory here.
 */

export interface BoundedQueueOptions<T> {
  maxEntries: number;
  maxBytes: number;
  /** Cheap estimator. Never JSON.stringify on the hot path. */
  sizeOf: (value: T) => number;
}

export class BoundedQueue<T> {
  readonly #maxEntries: number;
  readonly #maxBytes: number;
  readonly #sizeOf: (value: T) => number;
  #entries: T[] = [];
  #sizes: number[] = [];
  #bytes = 0;
  #dropped = 0;

  constructor(options: BoundedQueueOptions<T>) {
    this.#maxEntries = Math.max(1, Math.floor(options.maxEntries));
    this.#maxBytes = Math.max(1, Math.floor(options.maxBytes));
    this.#sizeOf = options.sizeOf;
  }

  push(entry: T): void {
    let size = 0;
    try {
      size = this.#sizeOf(entry);
    } catch {
      size = 0;
    }
    if (!Number.isFinite(size) || size < 0) size = 0;
    this.#entries.push(entry);
    this.#sizes.push(size);
    this.#bytes += size;
    this.#trim();
  }

  /** Take everything. The drop counter deliberately survives a drain. */
  drain(): T[] {
    const out = this.#entries;
    this.#entries = [];
    this.#sizes = [];
    this.#bytes = 0;
    return out;
  }

  /**
   * Put entries back at the head (a flush that could not be sent), then re-trim
   * oldest-first so the bounds still hold.
   */
  restore(entries: readonly T[]): void {
    if (entries.length === 0) return;
    const sizes = entries.map((entry) => {
      try {
        const size = this.#sizeOf(entry);
        return Number.isFinite(size) && size > 0 ? size : 0;
      } catch {
        return 0;
      }
    });
    this.#entries = [...entries, ...this.#entries];
    this.#sizes = [...sizes, ...this.#sizes];
    this.#bytes += sizes.reduce((sum, size) => sum + size, 0);
    this.#trim();
  }

  takeDropped(): number {
    const value = this.#dropped;
    this.#dropped = 0;
    return value;
  }

  addDropped(count: number): void {
    if (Number.isFinite(count) && count > 0) this.#dropped += Math.floor(count);
  }

  get size(): number {
    return this.#entries.length;
  }

  get bytes(): number {
    return this.#bytes;
  }

  get dropped(): number {
    return this.#dropped;
  }

  clear(): void {
    this.#entries = [];
    this.#sizes = [];
    this.#bytes = 0;
    this.#dropped = 0;
  }

  #trim(): void {
    while (
      this.#entries.length > this.#maxEntries ||
      (this.#bytes > this.#maxBytes && this.#entries.length > 1)
    ) {
      this.#entries.shift();
      const size = this.#sizes.shift() ?? 0;
      this.#bytes -= size;
      this.#dropped += 1;
    }
    if (this.#bytes < 0) this.#bytes = 0;
  }
}

/**
 * Cheap size estimator for a record-shaped object: character counts, not a
 * serialisation. Stringifying every record twice on the hot path is the kind of
 * cost that turns telemetry into a stutter.
 */
export function estimateSize(value: unknown, depth = 0): number {
  if (value === null || value === undefined) return 4;
  const kind = typeof value;
  if (kind === "string") return (value as string).length + 2;
  if (kind === "number" || kind === "boolean") return 8;
  if (depth > 3) return 8;
  if (Array.isArray(value)) {
    let total = 2;
    for (let i = 0; i < value.length && i < 32; i += 1) {
      total += estimateSize(value[i], depth + 1) + 1;
    }
    return total;
  }
  if (kind === "object") {
    let total = 2;
    let seen = 0;
    for (const key in value as Record<string, unknown>) {
      if (seen >= 48) break;
      total += key.length + 3;
      total += estimateSize((value as Record<string, unknown>)[key], depth + 1);
      seen += 1;
    }
    return total;
  }
  return 8;
}
